import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import api from '../services/api';
import { ESTADOS_BRASIL } from '../constants/estados';
import { Icon } from '../components/participant/Icon';
import { ParticipantTopBar } from '../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../theme/participantTheme';

interface UserProfile {
  id: string;
  name: string;
  email?: string;
  cpf: string;
  city: string;
  state: string;
  phone?: string;
  alternatePhone?: string;
  birthDate?: string;
  gender?: string;
  cnpj?: string;
  role: string;
  razaoSocial?: string;
}

const inputSx = {
  width: '100%',
  border: `2px solid ${c.border}`,
  borderRadius: `${c.radius.input}px`,
  background: c.surface,
  fontFamily: c.fontFamily,
  fontSize: 17,
  fontWeight: 600,
  color: c.textBase,
  padding: '15px 16px',
  outline: 'none',
  '&:focus': { borderColor: c.brandMid },
};

const labelSx = { fontSize: 15, fontWeight: 800, color: c.textDarkest, display: 'block', mb: '8px' };

const EditProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tab, setTab] = useState<'dados' | 'senha'>('dados');
  const [showPassword, setShowPassword] = useState(false);
  const [flash, setFlash] = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', cpf: '', city: '', state: '', phone: '',
    alternatePhone: '', birthDate: '', gender: '', cnpj: '', razaoSocial: '',
  });

  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await api.get('/users/profile');
        const userData = response.data;
        setProfile(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          cpf: userData.cpf || '',
          city: userData.city || '',
          state: userData.state || '',
          phone: userData.phone || '',
          alternatePhone: userData.alternatePhone || '',
          birthDate: userData.birthDate ? userData.birthDate.split('T')[0] : '',
          gender: userData.gender || '',
          cnpj: userData.cnpj || '',
          razaoSocial: userData.razaoSocial || '',
        });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar perfil');
      } finally {
        setLoadingProfile(false);
      }
    };
    if (user) fetchProfile();
  }, [user]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(''), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  const isAssociation = profile?.role === 'ASSOCIACAO';
  const isUsuarioFinal = profile?.role === 'USUARIO_FINAL';

  const setField = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));

  // Máscara progressiva 000.000.000-00 — o backend exige esse formato
  // exato, então formatamos enquanto o usuário digita em vez de só
  // validar no fim (mesmo esquema usado no cadastro).
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    let formatted = digits;
    if (digits.length > 9) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    else if (digits.length > 6) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0, 3)}.${digits.slice(3)}`;
    setFormData((prev) => ({ ...prev, cpf: formatted }));
  };

  const setPasswordField = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError('');
    setPasswordData((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleSubmitProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const updateData: any = { name: formData.name, cpf: formData.cpf, city: formData.city, state: formData.state };
      if (!isUsuarioFinal) updateData.email = formData.email;
      if (formData.phone) updateData.phone = formData.phone;
      if (formData.alternatePhone) updateData.alternatePhone = formData.alternatePhone;
      if (formData.birthDate) updateData.birthDate = formData.birthDate;
      if (formData.gender) updateData.gender = formData.gender;
      if (formData.cnpj) updateData.cnpj = formData.cnpj;
      if (formData.razaoSocial) updateData.razaoSocial = formData.razaoSocial;

      await api.put('/users/profile', updateData);
      showSnackbar('Perfil atualizado com sucesso!', 'success');
      setFlash('Dados atualizados com sucesso!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('As senhas não conferem');
      return;
    }

    setLoading(true);
    try {
      await api.put('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      showSnackbar('Senha alterada com sucesso!', 'success');
      setFlash('Senha alterada com sucesso!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: c.gradients.pageBg }}>
        <CircularProgress sx={{ color: c.brandMid }} size={48} />
      </Box>
    );
  }

  const initial = profile?.name?.trim()?.[0]?.toUpperCase() || '?';
  const passwordsMismatch = passwordData.confirmPassword.length > 0 && passwordData.newPassword !== passwordData.confirmPassword;

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <ParticipantTopBar />

        {/* Cabeçalho */}
        <Box
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.card}px`,
            padding: '20px',
            boxShadow: c.shadows.card,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/dashboard')}
            title="Voltar"
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              border: `2px solid ${c.border}`,
              borderRadius: `${c.radius.tile}px`,
              background: c.surfaceHover,
              color: c.brandDark,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
            }}
          >
            <Icon name="arrow_back" size={24} />
          </Box>
          <Box
            sx={{
              width: 62,
              height: 62,
              flexShrink: 0,
              borderRadius: '50%',
              background: c.gradients.primaryButton,
              color: c.onDarkPrimary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
            }}
          >
            {initial}
          </Box>
          <Box>
            <Box component="div" sx={{ fontSize: 20, fontWeight: 800, color: c.textDarkest }}>
              {profile?.name}
            </Box>
            <Box component="div" sx={{ fontSize: 14, color: c.textSecondary, mt: '2px' }}>
              Meu perfil
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {[
            { key: 'dados' as const, label: 'Meus dados', icon: 'person' },
            { key: 'senha' as const, label: 'Senha', icon: 'lock' },
          ].map((t) => (
            <Box
              key={t.key}
              component="button"
              onClick={() => setTab(t.key)}
              sx={{
                minHeight: 52,
                border: 'none',
                borderRadius: '12px',
                background: tab === t.key ? c.surfaceDark : 'transparent',
                color: tab === t.key ? c.onDarkPrimary : c.textSecondary,
                fontFamily: c.fontFamily,
                fontSize: 15.5,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Icon name={t.icon} size={20} />
              {t.label}
            </Box>
          ))}
        </Box>

        {flash && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: c.surfaceTint,
              border: `2px solid ${c.brandMid}`,
              borderRadius: `${c.radius.tile}px`,
              padding: '14px 16px',
              animation: 'participantFadeUp 0.3s ease both',
            }}
          >
            <Icon name="check_circle" size={22} color={c.brandMid} />
            <Box component="span" sx={{ fontSize: 16, fontWeight: 700, color: c.successText }}>
              {flash}
            </Box>
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', background: c.errorBg, border: `2px solid ${c.errorBorder}`, borderRadius: `${c.radius.input}px`, padding: '13px 15px' }}>
            <Icon name="error" size={22} color={c.error} />
            <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.errorTextDark }}>
              {error}
            </Box>
          </Box>
        )}

        {tab === 'dados' && (
          <Box
            component="form"
            onSubmit={handleSubmitProfile}
            sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, padding: '22px', boxShadow: c.shadows.card, display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <Box>
              <Box component="label" sx={labelSx}>{isAssociation ? 'Nome da associação' : 'Nome completo'}</Box>
              <Box component="input" required value={formData.name} onChange={setField('name')} disabled={loading} sx={inputSx} />
            </Box>

            {!isUsuarioFinal && (
              <Box>
                <Box component="label" sx={labelSx}>Email</Box>
                <Box component="input" type="email" required value={formData.email} onChange={setField('email')} disabled={loading} sx={inputSx} />
              </Box>
            )}

            {isAssociation && (
              <Box>
                <Box component="label" sx={labelSx}>Razão social</Box>
                <Box component="input" value={formData.razaoSocial} onChange={setField('razaoSocial')} disabled={loading} sx={inputSx} />
              </Box>
            )}

            <Box>
              <Box component="label" sx={labelSx}>CPF</Box>
              <Box component="input" required value={formData.cpf} onChange={handleCpfChange} inputMode="numeric" disabled={loading} sx={inputSx} />
            </Box>

            {isAssociation && (
              <Box>
                <Box component="label" sx={labelSx}>CNPJ</Box>
                <Box component="input" value={formData.cnpj} onChange={setField('cnpj')} disabled={loading} sx={inputSx} />
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <Box>
                <Box component="label" sx={labelSx}>Telefone</Box>
                <Box component="input" placeholder="(00) 00000-0000" value={formData.phone} onChange={setField('phone')} disabled={loading} sx={inputSx} />
              </Box>
              <Box>
                <Box component="label" sx={labelSx}>Data de nascimento</Box>
                <Box component="input" type="date" value={formData.birthDate} onChange={setField('birthDate')} disabled={loading} sx={inputSx} />
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <Box>
                <Box component="label" sx={labelSx}>Cidade</Box>
                <Box component="input" required value={formData.city} onChange={setField('city')} disabled={loading} sx={inputSx} />
              </Box>
              <Box>
                <Box component="label" sx={labelSx}>Estado</Box>
                <Box component="select" required value={formData.state} onChange={setField('state') as any} disabled={loading} sx={{ ...inputSx, appearance: 'auto', padding: '15px 12px' }}>
                  <option value="">UF</option>
                  {ESTADOS_BRASIL.map((estado) => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box
              component="button"
              type="submit"
              disabled={loading}
              sx={{
                width: '100%',
                minHeight: 58,
                border: 'none',
                borderRadius: `${c.radius.button}px`,
                background: c.gradients.primaryButton,
                color: c.onDarkPrimary,
                fontFamily: c.fontFamily,
                fontSize: 17,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: c.shadows.primaryButton,
                opacity: loading ? 0.85 : 1,
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: c.onDarkPrimary }} /> : <Icon name="save" size={22} />}
              {loading ? 'Salvando...' : 'Salvar alterações'}
            </Box>
          </Box>
        )}

        {tab === 'senha' && (
          <Box
            component="form"
            onSubmit={handleSubmitPassword}
            sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, padding: '22px', boxShadow: c.shadows.card, display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {passwordError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', background: c.errorBg, border: `2px solid ${c.errorBorder}`, borderRadius: `${c.radius.input}px`, padding: '13px 15px' }}>
                <Icon name="error" size={20} color={c.error} />
                <Box component="span" sx={{ fontSize: 14.5, fontWeight: 700, color: c.errorTextDark }}>
                  {passwordError}
                </Box>
              </Box>
            )}

            <Box>
              <Box component="label" sx={labelSx}>Senha atual</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', border: `2px solid ${c.border}`, borderRadius: `${c.radius.input}px`, background: c.surface, pr: '6px' }}>
                <Box
                  component="input"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={passwordData.currentPassword}
                  onChange={setPasswordField('currentPassword')}
                  disabled={loading}
                  sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: c.fontFamily, fontSize: 17, fontWeight: 600, color: c.textBase, padding: '15px 16px' }}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  sx={{ width: 44, height: 44, border: 'none', borderRadius: '10px', background: 'transparent', color: c.brandIcon, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={22} />
                </Box>
              </Box>
            </Box>

            <Box>
              <Box component="label" sx={labelSx}>Nova senha</Box>
              <Box component="input" type={showPassword ? 'text' : 'password'} required value={passwordData.newPassword} onChange={setPasswordField('newPassword')} disabled={loading} sx={inputSx} />
              <Box component="div" sx={{ fontSize: 13, color: c.textTertiary, mt: '6px' }}>
                Mínimo 8 caracteres
              </Box>
            </Box>

            <Box>
              <Box component="label" sx={labelSx}>Confirmar nova senha</Box>
              <Box
                component="input"
                type={showPassword ? 'text' : 'password'}
                required
                value={passwordData.confirmPassword}
                onChange={setPasswordField('confirmPassword')}
                disabled={loading}
                sx={{ ...inputSx, borderColor: passwordsMismatch ? c.error : c.border }}
              />
              {passwordsMismatch && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '6px' }}>
                  <Icon name="error" size={16} color={c.error} />
                  <Box component="span" sx={{ fontSize: 13, color: c.error, fontWeight: 700 }}>
                    As senhas não conferem
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              component="button"
              type="submit"
              disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
              sx={{
                width: '100%',
                minHeight: 58,
                border: 'none',
                borderRadius: `${c.radius.button}px`,
                fontFamily: c.fontFamily,
                fontSize: 17,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                ...(!loading && passwordData.currentPassword && passwordData.newPassword && passwordData.newPassword === passwordData.confirmPassword
                  ? { background: c.gradients.primaryButton, color: c.onDarkPrimary, cursor: 'pointer', boxShadow: c.shadows.primaryButton }
                  : { background: c.border, color: c.textFaint, cursor: 'not-allowed' }),
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <Icon name="lock_reset" size={22} />}
              {loading ? 'Alterando...' : 'Alterar senha'}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditProfile;
